import { PlanogramProductHoverContent } from "@/components/planogram/PlanogramProductHoverContent";
import { PlanogramProductPopoverContent } from "@/components/coverage-beta/coverage-beta-right-panel/coverage-beta-planogram-view/PlanogramProductPopoverContent";
import { activatePlanoProduct } from "@/components/planogram/planoProductClickBridge";
import {
  selectActiveAreaIdx,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import {
  PlanogramProduct,
  type PlanogramProductRenderer,
  type PlanogramRenderedProduct,
} from "@augmodo/reusable-components";
import { HoverCard, Popover } from "@mantine/core";
import { useEffect } from "react";
// The pulse ring's keyframes, colocated with the rect that runs them.
import "./CoverageBetaPlanogramProduct.css";
import { create } from "zustand";

// The pulse channel between the view's framing effects and the cells. Its own
// module-local store rather than a slice of useCoverageBetaStore: the pulse is
// transient chrome nothing else reads, and the store exists only because the
// static render prop leaves no props path into the cells. Nonce rather than a
// boolean so a repeat framing of the same product replays the animation.
const usePlanogramPulseStore = create<{ productId: string; nonce: number }>(
  () => ({ productId: "", nonce: 0 }),
);

/** Replay the red "ping" on this product's planogram placements. */
export const pulsePlanogramProduct = (productId: string) =>
  usePlanogramPulseStore.setState((s) => ({
    productId,
    nonce: s.nonce + 1,
  }));

// Which placement's admin popover is open (planogram_product_id, so only the
// clicked facing opens — matching the legacy ActivePlanoProductPopover, which
// also keyed on the placement). Same module-local-store pattern as the pulse
// channel, and for the same reason: the render prop is a static function, so
// the view above can only reach the cells through a store. The view reads it
// to freeze wheel-zoom while a popover is open (the reusable viewer does this
// for its built-in popover; a custom one must do it itself — see the
// `enableZoom` prop docs) and to dismiss it when the anchor goes stale.
//
// Module-global, so every mounted CoverageBetaPlanogramView would share it —
// deliberate, matching the one-instance assumption the whole feature already
// makes (useCoverageBetaStore, the useActivePlanoProductInfoStore click
// bridge, and the pulse channel above are all singletons too; CoverageBetaTask
// is the sole mount site). If coverage-beta ever mounts two viewers at once,
// scope all of these per instance together, not just this one.
const usePlanogramPopoverStore = create<{ openId: string }>(() => ({
  openId: "",
}));

/** True while any planogram product popover (admin view) is open. */
export const useIsPlanogramPopoverOpen = () =>
  usePlanogramPopoverStore((s) => s.openId !== "");

/** Dismiss the open planogram product popover, if any. */
export const closePlanogramProductPopover = () =>
  usePlanogramPopoverStore.setState({ openId: "" });

interface Props {
  item: PlanogramRenderedProduct;
}

/**
 * One product cell of the reusable planogram viewer, coverage-beta flavored:
 * selected keeps the viewer's red active outline, a hover-only highlight gets
 * the teal used by the shelf detection boxes (see OneLabelBox), and clicks
 * feed the useActivePlanoProductInfoStore bridge that drives the selection
 * logic in CoverageBetaPlanogramView.
 *
 * Per-view chrome, matching the legacy viewer's split:
 * - Labeler view: hovering shows a product hover card (clicks are taken by
 *   the selection bridge, so a click popover would fight it).
 * - Admin/read-only view: clicking opens a details popover (clicks mutate
 *   nothing there, so the click is free to inspect).
 * Both render through Mantine with `withinPortal`: the cell lives inside the
 * viewer's SVG, and the portal is what lets the dropdown be HTML.
 *
 * Subscribes to the store itself (isReadOnly, its own isSelected) instead of
 * receiving them as props, so the render prop below can stay a module-level
 * constant: the viewer's per-cell memo compares `children` by reference, and a
 * static function means a hover or selection change re-renders only the cells
 * whose own answer changed — never the whole planogram.
 */
export const CoverageBetaPlanogramProduct = ({ item }: Props) => {
  const isReadOnly = useCoverageBetaStore((s) => s.isReadOnly);
  // Stable setter, so subscribing to it keeps this cell's selector discipline
  // (every other read here is a boolean/scalar) intact.
  const setLocatedProductId = useCoverageBetaStore(
    (s) => s.setLocatedProductId,
  );
  // Boolean selector, so this cell re-renders only when ITS membership in the
  // active area's selection flips — not on every selection change.
  const isSelected = useCoverageBetaStore((s) => {
    const area = s.idxToFeEmptyArea[selectActiveAreaIdx(s)];
    return (
      area?.selectedProducts.some((p) => p.id === item.product.product_id) ??
      false
    );
  });
  const isHighlighted = item.isActive && !isSelected;
  // Non-zero only for the product last framed by a zoomToProducts call — the
  // trigger for the red "ping" below. Scoped to this cell's product, so a
  // pulse re-renders only the pinged placements.
  const pulseNonce = usePlanogramPulseStore((s) =>
    s.productId === item.product.product_id ? s.nonce : 0,
  );
  // Boolean selector for the same reason as isSelected: opening a popover
  // re-renders exactly the two cells whose answer changed.
  const isPopoverOpen = usePlanogramPopoverStore(
    (s) => s.openId === item.product.id,
  );

  // If this placement unmounts while it owns the open popover — the product
  // list can refetch/change under a mounted viewer and drop it, with none of
  // the view-level dismissal deps (tab/planogram/reset) changing — clear the
  // store. The Popover disappears with the cell either way; what a stale
  // openId would leave behind is `useIsPlanogramPopoverOpen() === true` with
  // nothing on screen, freezing wheel-zoom for good. Also runs when the cell
  // is reused for a different placement (dep change), which is the same
  // "this anchor is gone" case.
  useEffect(() => {
    const placementId = item.product.id;
    return () => {
      if (usePlanogramPopoverStore.getState().openId === placementId) {
        closePlanogramProductPopover();
      }
    };
  }, [item.product.id]);

  // Same "this anchor is gone" cleanup for the locate cue below: a products
  // refetch can drop a hovered cell, and onMouseLeave never fires — which would
  // strand a teal highlight on the query image with nothing pointing at it.
  // Mirrors the store's defensive neighbourProductIds clear on area delete.
  useEffect(() => {
    const productId = item.product.product_id;
    return () => {
      if (useCoverageBetaStore.getState().locatedProductId === productId) {
        setLocatedProductId("");
      }
    };
  }, [item.product.product_id, setLocatedProductId]);

  const productEl = (
    <PlanogramProduct
      {...item}
      // Suppress the built-in SVG <title>: the hover card (labeler) and
      // popover (admin) replace it, and the native browser tooltip would
      // double up on top of them.
      label=""
      activeOutlineColor={isHighlighted ? "#0d9488" : undefined}
      style={{ cursor: "pointer" }}
      // The reverse hover-to-locate (DTOPS-272): point at the product's
      // detection box(es) on the query image, the companion to the query
      // image's own hover lighting up this planogram cell. Handlers land on
      // the cell's <g> — PlanogramProductProps extends SVGProps<SVGGElement>.
      onMouseEnter={() => setLocatedProductId(item.product.product_id)}
      onMouseLeave={() => setLocatedProductId("")}
      onClick={
        isReadOnly
          ? // Admin: toggle this placement's details popover. The selection
            // bridge ignores read-only clicks anyway, so route them here.
            () =>
              usePlanogramPopoverStore.setState((s) => ({
                openId: s.openId === item.product.id ? "" : item.product.id,
              }))
          : () => activatePlanoProduct(item)
      }
    />
  );

  return (
    <>
      {isReadOnly ? (
        <Popover
          opened={isPopoverOpen}
          onChange={(opened) => {
            if (!opened) closePlanogramProductPopover();
          }}
          position="right-start"
          offset={8}
          withinPortal
          shadow="md"
          middlewares={{ flip: true, shift: true }}
        >
          <Popover.Target>{productEl}</Popover.Target>
          <Popover.Dropdown p={12}>
            <PlanogramProductPopoverContent item={item} />
          </Popover.Dropdown>
        </Popover>
      ) : (
        <HoverCard
          position="right-start"
          offset={8}
          withinPortal
          shadow="md"
          // Same 500ms the legacy OneLabelBox tooltip waited before showing.
          openDelay={500}
          closeDelay={0}
          middlewares={{ flip: true, shift: true }}
          // Purely informational — never intercept the mouse on its way to
          // neighbouring products (the reference-image hover card does the same).
          styles={{ dropdown: { pointerEvents: "none" } }}
        >
          <HoverCard.Target>{productEl}</HoverCard.Target>
          <HoverCard.Dropdown p={8}>
            <PlanogramProductHoverContent item={item} />
          </HoverCard.Dropdown>
        </HoverCard>
      )}
      {/* Hover-highlight emphasis: a filled overlay on top of the pack shot,
          matching the legacy OneLabelBox treatment (teal fill at 0.22 + ~4px
          of border+glow) so the left-image → planogram hover cue reads at a
          glance instead of as a thin outline. Drawn after the product so it
          paints above it; pointer-events off so hover/click still land on the
          product plate beneath. non-scaling-stroke keeps the border the same
          screen pixels at every zoom, like the viewer's own outlines. */}
      {isHighlighted && (
        <rect
          x={item.box.x}
          y={item.box.y}
          width={item.box.width}
          height={item.box.height}
          fill="#0d9488"
          fillOpacity={0.22}
          stroke="#0d9488"
          strokeWidth={4}
          vectorEffect="non-scaling-stroke"
          pointerEvents="none"
        />
      )}
      {/* Red "ping" on a freshly-framed selection — the legacy pulseOnFrame,
          as an expanding stroke ring (keyframes in the sibling CSS file).
          Keyed on the
          nonce so a repeat framing remounts the rect and replays the
          animation; base stroke-opacity 0 keeps it invisible once the
          animation ends without needing an unmount timer. */}
      {pulseNonce > 0 && isSelected && (
        <rect
          key={pulseNonce}
          x={item.box.x}
          y={item.box.y}
          width={item.box.width}
          height={item.box.height}
          fill="#ef4444"
          fillOpacity={0}
          stroke="#ef4444"
          strokeOpacity={0}
          vectorEffect="non-scaling-stroke"
          pointerEvents="none"
          style={{ animation: "coverage-beta-plano-pulse 0.7s ease-out 3" }}
        />
      )}
    </>
  );
};

/**
 * The render prop handed to the reusable viewer — the adapter between its
 * calling convention (a plain `children(item, index)` call) and this
 * component. Module-level so its identity never changes: the viewer's
 * per-cell memo compares `children` by reference, and a stable function keeps
 * a hover or selection change from re-rendering every product cell. Never
 * move this inside a component body.
 */
export const renderCoverageBetaPlanogramProduct: PlanogramProductRenderer = (
  item,
) => <CoverageBetaPlanogramProduct item={item} />;
